import type { QueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import type { useActiveRealmAPIContext } from '@interxfi/api-client';
import { useAssetLogo } from '@interxfi/api-client/asset/use-asset-logo';
import {
  buildIxcAssetsV1AssetQueryServiceAssetGetQueryOptions,
  useIxcAssetsV1AssetQueryServiceAssetGet,
} from '@interxfi/api-client/generated/hooks/ixc-assets-v1-asset-query-service-asset-get';
import { DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE, PageLayout } from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import { PageTabRail } from '../../../../../../../components/page-tab-rail/page-tab-rail';
import { type PortalTabbedNavigationTab, TabbedNavigationGroup } from '../../../../../../../components/router/router-tabbed-navigation';
import { getAssetMarketDataPath } from '../../../../../../../features/asset/asset-routing';
import { getAssetName, getAssetSymbol, getIsAssetEquity } from '../../../../../../../features/asset/utils';
import { MarketDataChart, prefetchMarketDataChartPeriods } from '../../../../../../../features/market-data/market-data-chart';
import { MarketDataCompanyOverview } from '../../../../../../../features/market-data/market-data-company-overview';
import { MarketDataHeadlines } from '../../../../../../../features/market-data/market-data-headlines';
import { MarketDataInspectionProvider } from '../../../../../../../features/market-data/market-data-inspection';
import { MarketDataQuoteDetails } from '../../../../../../../features/market-data/market-data-quote-details';
import { MarketDataQuoteHeader } from '../../../../../../../features/market-data/market-data-quote-header';
import { MarketDataQuoteStatus } from '../../../../../../../features/market-data/market-data-quote-status';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/asset/_asset/$assetId/_detail/market-data')({
  staticData: {
    tab: {
      group: TabbedNavigationGroup.AssetMarketData,
      label: 'Overview',
      order: 0,
    },
  } as const satisfies PortalTabbedNavigationTab<TabbedNavigationGroup.AssetMarketData>,
  loader: ({ context, params }) => {
    void prefetchAssetMarketDataChart(context.queryClient, context.apiContext, context.isAuthenticated, params.assetId);

    return {
      breadcrumb: buildBreadcrumb({
        id: `asset-${params.assetId}-market-data`,
        label: 'Market Data',
        to: getAssetMarketDataPath(params.assetId),
      }),
    } satisfies AggregateBreadcrumb;
  },
  head: () => getPageHead('Market Data'),
  component: AssetMarketData,
});

async function prefetchAssetMarketDataChart(
  queryClient: QueryClient,
  apiContext: ReturnType<typeof useActiveRealmAPIContext>,
  isAuthenticated: boolean,
  assetId: string,
) {
  const { activeRealm, activeTenantContext, baseUrl, extendKey, token } = apiContext;
  const assetOptions = buildIxcAssetsV1AssetQueryServiceAssetGetQueryOptions(
    baseUrl,
    isAuthenticated,
    token,
    activeRealm,
    activeTenantContext,
    extendKey,
    queryClient.getQueryCache(),
    { assetId },
  );

  if (assetOptions.enabled !== true) {
    return;
  }

  const assetData = await queryClient.query({ ...assetOptions, staleTime: 'static' }).catch(() => undefined);
  const type = assetData?.asset?.data?.type;
  const symbol = getAssetSymbol(type);

  if (!symbol || !getIsAssetEquity(type)) {
    return;
  }

  await prefetchMarketDataChartPeriods(queryClient, apiContext, isAuthenticated, symbol);
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load asset', 'view this asset');

function AssetMarketData() {
  const { assetId } = Route.useParams();
  const { data, error, isPending } = useIxcAssetsV1AssetQueryServiceAssetGet({ assetId });
  useErrorHandler(error, errorMessageGetter);
  const { logoSrc, isPending: logoIsPending } = useAssetLogo(assetId);
  const name = getAssetName(data?.asset?.data?.type);
  const symbol = getAssetSymbol(data?.asset?.data?.type);
  const assetShowPending = Boolean(isPending || error);
  const showPending = assetShowPending || logoIsPending;
  const hasMarketData = Boolean(symbol) || assetShowPending;

  return (
    <PageLayout gap={7} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageTabRail group={TabbedNavigationGroup.AssetMarketData} />
      </PageLayout.Section>

      <PageLayout.Section>
        <WrappingLayoutContainer
          gap={10}
          left={{
            ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
            render: (
              <MarketDataInspectionProvider>
                <VerticalStack gap={7}>
                  <VerticalStack gap={4}>
                    {hasMarketData ? <MarketDataQuoteStatus isPending={assetShowPending} symbol={symbol} /> : null}

                    <MarketDataQuoteHeader
                      hasMarketData={hasMarketData}
                      isPending={assetShowPending}
                      logoSrc={logoSrc}
                      name={name}
                      showPending={showPending}
                      symbol={symbol}
                    />
                  </VerticalStack>

                  {hasMarketData ? <MarketDataChart isPending={assetShowPending} symbol={symbol} /> : null}

                  {hasMarketData ? <MarketDataQuoteDetails isPending={assetShowPending} symbol={symbol} /> : null}
                </VerticalStack>
              </MarketDataInspectionProvider>
            ),
          }}
          padding={LAYOUT_ZERO_CONTAINER_PADDING}
          right={{
            ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
            render: hasMarketData ? (
              <VerticalStack gap={7}>
                <MarketDataCompanyOverview isPending={assetShowPending} symbol={symbol} />
                <MarketDataHeadlines isPending={assetShowPending} symbol={symbol} />
              </VerticalStack>
            ) : null,
          }}
          wrap="forward"
          wrapGap={7}
        />
      </PageLayout.Section>
    </PageLayout>
  );
}
